import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts'
import type { Device } from '@/types/device.types'

interface RainfallChartProps {
  devices: Device[]
}

interface ChartDataPoint {
  name: string
  rainfall: number
  deviceId: string
}

// Helper function to get rainfall from measurement
const getRainfallMm = (device: Device): number => {
  if (!device.lastMeasurement || typeof device.lastMeasurement !== 'object') {
    return -1
  }

  const measurement = device.lastMeasurement as any

  // For watersense devices - rain is stored in raw units, multiply by 0.2 to get mm
  if (device.deviceType === 'watersense' && measurement.rain !== undefined) {
    return measurement.rain * 0.2
  }

  // For rk900 devices - rainfall_mm is already in mm
  if (device.deviceType === 'rk900' && measurement.rainfall_mm !== undefined) {
    return measurement.rainfall_mm
  }

  return -1
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null

  const data = payload[0].payload as ChartDataPoint

  return (
    <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
      <p className="font-bold">{data.name}</p>
      <p className="text-sm">Rain: {data.rainfall.toFixed(2)} mm</p>
    </div>
  )
}

export const RainfallChart = ({ devices }: RainfallChartProps) => {
  const navigate = useNavigate()

  // Filter devices with rainfall data and sort by rainfall (highest first)
  const devicesWithRainfall = devices
    .map((device) => ({
      device,
      rainfall: getRainfallMm(device)
    }))
    .filter((item) => item.rainfall >= 0)
    .sort((a, b) => b.rainfall - a.rainfall)
    .slice(0, 10) // Take top 10

  if (devicesWithRainfall.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            No devices with rain buckets found.
          </p>
          <p className="text-sm text-muted-foreground">
            Add a device with a rain bucket to see it here.
          </p>
        </div>
      </div>
    )
  }

  const chartData: ChartDataPoint[] = devicesWithRainfall.map(({ device, rainfall }) => ({
    name: device.name,
    rainfall: parseFloat(rainfall.toFixed(2)),
    deviceId: device.id
  }))

  const handleBarClick = (data: ChartDataPoint) => {
    navigate(`/devices/${data.deviceId}`)
  }

  return (
    <div className="w-full h-[350px]">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Top 10 Recent Rainfall</h3>
        <p className="text-sm text-muted-foreground">
          For devices in your linked accounts + favorites
        </p>
      </div>

      <ResponsiveContainer width="100%" height="85%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 12 }}
            style={{ cursor: 'pointer' }}
          />
          <YAxis
            label={{ value: 'Rainfall in last 7 days (mm)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="rainfall"
            fill="#008080"
            onClick={handleBarClick}
            cursor="pointer"
          >
            <LabelList
              dataKey="rainfall"
              position="top"
              formatter={(value: number) => `${value.toFixed(2)} mm`}
              style={{ fontSize: 12, fontWeight: 'bold' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
